import { CAMPAIGN_STATUS } from "@/@types/campaign";

import { COMMON_TEXTS } from "../../utils/messages";

export const CAMPAIGN_TYPES = {
  PRO013: "PRO013",
  PRO014: "PRO014",
  PRO017: "PRO017",
} as const;

export type CampaignTypeKey = keyof typeof CAMPAIGN_TYPES;
export type CampaignTypeValue = typeof CAMPAIGN_TYPES[CampaignTypeKey];

export { CAMPAIGN_STATUS };
export type { CampaignStatusKey, CampaignStatusValue } from "@/@types/campaign";

export const STATUS_CONFIG = {
  [CAMPAIGN_STATUS.ACTIVE]: {
    label: "Ativa",
    className: "bg-green-100 text-green-700 border-green-300",
    info: "Campanhas ativas permitem editar Descrição, Data de Término (não pode reduzir) e Lojas (exceto campanhas globais)",
  },
  [CAMPAIGN_STATUS.FUTURE]: {
    label: "Futura",
    className: "bg-blue-100 text-blue-700 border-blue-300",
    info: "Campanhas futuras permitem editar todos os campos, incluindo Lojas (exceto campanhas globais)",
  },
  [CAMPAIGN_STATUS.EXPIRED]: {
    label: "Expirada",
    className: "bg-gray-100 text-gray-700 border-gray-300",
    info: "Campanhas expiradas não podem ser editadas",
  },
} as const;

export const FIELD_LIMITS = {
  DESCRIPTION: {
    MIN: 3,
    MAX: 100,
  },
  DEPARTMENT_CODE: {
    MIN: 1,
    MAX: 99,
  },
  GROUP_CODE: {
    MIN: 1,
    MAX: 99,
  },
} as const;

export const ERROR_MESSAGES = {
  DESCRIPTION_MIN: `Descrição deve ter no mínimo ${FIELD_LIMITS.DESCRIPTION.MIN} caracteres`,
  DESCRIPTION_MAX: `Descrição deve ter no máximo ${FIELD_LIMITS.DESCRIPTION.MAX} caracteres`,
  START_DATE_REQUIRED: "Data de início é obrigatória",
  END_DATE_REQUIRED: "Data de término é obrigatória",
  END_DATE_INVALID: "Data de término deve ser maior ou igual à data de início",
  END_DATE_CANNOT_REDUCE: "Não é possível reduzir a data de término de uma campanha ativa",
} as const;

export const ALL_STORES_CODE = 999;

export const PRO014_STORES_TEXTS = {
  TITLE: "Lojas da campanha",
  ALL_STORES_SCOPE: "Vale para todas as lojas.",
  LEGACY_NOTICE:
    "Campanha no modelo antigo: vale só para lojas específicas, sem a opção de todas as lojas.",
  NO_STORES: "Nenhuma loja vinculada a esta campanha.",
  STORE_LABEL: COMMON_TEXTS.STORE_LABEL,
  STORE_WITH_NAME: (storeCode: number, name: string): string =>
    `${COMMON_TEXTS.STORE_LABEL(storeCode)} — ${name}`,
  WINDOW_LABEL: (start: string, end: string): string => `${start} a ${end}`,
  ENDED_AT: (date: string): string => `encerrada em ${date}`,
  PENDING_CHANGES: "alterações pendentes",
  PROCESSING: "em processamento",
  PREVIEW_TITLE: "O que muda ao salvar",
  SWITCH_TO_ALL: "Passa a valer em todas as lojas. As lojas específicas ficam guardadas, sem uso.",
  SWITCH_TO_SPECIFIC:
    "Passa a valer só nas lojas escolhidas. As que já tinham registro na campanha são reaproveitadas, sem duplicar.",
  CHOSEN: "lojas escolhidas",
  ADD: "+ adicionar",
  REOPEN: "↺ reabrir",
  END: "− encerrar a partir de ontem",
  CONFIRM_ALL_TITLE: "Aplicar a campanha em todas as lojas?",
  CONFIRM_ALL_DESCRIPTION:
    "A campanha passa a valer em todas as lojas da rede. As lojas específicas continuam registradas, mas deixam de ser usadas enquanto a campanha valer para todas.",
  CONFIRM: COMMON_TEXTS.CONFIRM,
  BACK: COMMON_TEXTS.BACK,
  SAVE: "Salvar lojas",
  SELECT_AT_LEAST_ONE: "Selecione pelo menos uma loja",
  UPDATED: (added: number, reopened: number, reset: number, ended: number): string =>
    `Lojas atualizadas: ${added} adicionada(s), ${reopened} reaberta(s), ${reset} reaproveitada(s) e ${ended} encerrada(s) a partir de ontem.`,
  LEGACY_UPDATED: (added: number, removed: number): string =>
    `Lojas atualizadas: ${added} adicionada(s) e ${removed} encerrada(s) a partir de ontem.`,
  UNCHANGED: "Nenhuma alteração: as lojas da campanha já estavam assim.",
  SWITCHED_TO_ALL: "A campanha passou a valer em todas as lojas.",
  QUEUED: "A alteração das lojas foi enfileirada e será aplicada em instantes.",
  CHANGED_ELSEWHERE:
    "As lojas da campanha foram alteradas por outra pessoa. Recarregue a tela e tente de novo.",
  SAVE_BEFORE_LEAVING: "Salve as lojas ou desfaça a alteração antes de sair desta etapa.",
  SAVE_STORES_FIRST: "Salve as lojas ou desfaça a alteração antes de salvar os demais campos.",
} as const;
